import { z } from "zod";

import { PARTY_KIND_LABELS, PARTY_KINDS, type DirectoryEntry, type PartyKind } from "./directory";
import type { IntakeAuditEvent } from "./intake";

// Document links: which parties of the Directory each Intake document is about — the vendor an invoice is from, the
// customer a contract is with, the person an offer letter is for. A link joins one document to one party, and a
// document may be linked to several; the API keeps them in the document links table and lets an editor of the
// Directory link and unlink them. The API and the frontend share these.

/**
 * Who linked a document when no member did: the agent that reads a document for the parties it names. A link's
 * `linkedBy`, the work item's `createdBy` and `closedBy`, and the audit log's actor all say it.
 */
export const DOCUMENT_LINKER = "Document linker";

/**
 * The most parties one document may be linked to, by the Document linker and by hand together. A document names a
 * handful of parties at most; the limit is what keeps a document's list short enough to read, and what the API
 * refuses a link past.
 */
export const MAX_DOCUMENT_LINKS = 40;

/**
 * One link as the API keeps it: the document, the party, and when and by whom they were linked. The document's name,
 * content type and upload time are copied onto the link when it is made — a document never changes them — so a
 * party's documents are listed from its links alone, without reading the files table. The party's name is the one it
 * had when it was linked, which is what the link shows once the party has left the Directory.
 */
export const documentLinkSchema = z.object({
	/** The document's id in the files table. */
	fileId: z.string().min(1),
	document: z.object({
		/** The file name the document was uploaded with. */
		name: z.string().min(1).max(255),
		contentType: z.string().min(1),
		/** When it was uploaded, as an ISO 8601 timestamp in UTC. */
		uploadedAt: z.iso.datetime(),
	}),
	/** The party: its kind, its id in the table that owns it, and its name when it was linked. */
	party: z.object({
		kind: z.enum(PARTY_KINDS),
		id: z.string().min(1).max(64),
		name: z.string().min(1).max(160),
	}),
	/** When the two were linked, as an ISO 8601 timestamp in UTC. */
	linkedAt: z.iso.datetime(),
	/** Who linked them: `DOCUMENT_LINKER`, or the name of the member who did, as it was then. */
	linkedBy: z.string().min(1).max(80),
});
export type DocumentLink = z.infer<typeof documentLinkSchema>;

/** A party a document is linked to, as `GET /api/intake/files/{fileId}/parties` answers it. */
export const documentPartySchema = z.object({
	kind: z.enum(PARTY_KINDS),
	id: z.string().min(1).max(64),
	/**
	 * What the Directory calls the party now, or the name it was linked under once it has no row there any more: a party
	 * taken out of the Directory keeps its links.
	 */
	name: z.string().min(1).max(160),
	/** The line the Directory shows under the party's name, or null when the party is no longer in the Directory. */
	detail: z.string().min(1).max(140).nullable(),
	/** Whether the party still has a row in the Directory, so the page can link to it. */
	inDirectory: z.boolean(),
	linkedAt: z.iso.datetime(),
	linkedBy: z.string().min(1).max(80),
});
export type DocumentParty = z.infer<typeof documentPartySchema>;

/** Every party one document is linked to, A→Z by name (`documentPartiesFrom`). */
export const documentPartiesSchema = z.object({ parties: z.array(documentPartySchema).max(MAX_DOCUMENT_LINKS) });
export type DocumentParties = z.infer<typeof documentPartiesSchema>;

/**
 * The parties a document's `links` join, as the organization's directory `rows` stand now, A→Z by name — the order
 * the Directory lists them in — and two of one name by kind, then by id, so every read lists them the same way. A
 * party whose row is gone is listed by the name it was linked under, with no detail. The API answers every change to a
 * document's links with this.
 */
export function documentPartiesFrom(links: readonly DocumentLink[], rows: readonly DirectoryEntry[]): DocumentParties {
	const parties = links.map((link): DocumentParty => {
		const row = rows.find(entry => entry.kind === link.party.kind && entry.id === link.party.id);
		return {
			kind: link.party.kind,
			id: link.party.id,
			name: row?.name ?? link.party.name,
			detail: row?.detail ?? null,
			inDirectory: row !== undefined,
			linkedAt: link.linkedAt,
			linkedBy: link.linkedBy,
		};
	});
	return {
		parties: parties.toSorted(
			(a, b) => a.name.localeCompare(b.name) || a.kind.localeCompare(b.kind) || a.id.localeCompare(b.id),
		),
	};
}

/** What an editor sends to link a document to a party of the Directory. */
export const linkDocumentPartyRequestSchema = z.object({
	kind: z.enum(PARTY_KINDS),
	/** The party's id in the table that owns it, as its directory row names it. */
	partyId: z.string().min(1).max(64),
});
export type LinkDocumentPartyRequest = z.infer<typeof linkDocumentPartyRequestSchema>;

/** A document a party is linked to, as `GET /api/directory/{kind}/{partyId}/documents` answers it. */
export const partyDocumentSchema = z.object({
	/** The document's id in the files table, which is what its page is found by. */
	id: z.string().min(1),
	name: z.string().min(1).max(255),
	contentType: z.string().min(1),
	uploadedAt: z.iso.datetime(),
	linkedAt: z.iso.datetime(),
	linkedBy: z.string().min(1).max(80),
});
export type PartyDocument = z.infer<typeof partyDocumentSchema>;

/** Every document one party is linked to, the newest uploaded first. */
export const partyDocumentsSchema = z.object({ documents: z.array(partyDocumentSchema) });
export type PartyDocuments = z.infer<typeof partyDocumentsSchema>;

/** A document as a party's list of documents shows it, from the link alone. */
export function partyDocumentFrom(link: DocumentLink): PartyDocument {
	return {
		id: link.fileId,
		name: link.document.name,
		contentType: link.document.contentType,
		uploadedAt: link.document.uploadedAt,
		linkedAt: link.linkedAt,
		linkedBy: link.linkedBy,
	};
}

/** A party as an audit entry about a document's links names it: what it is called, and what kind of party it is. */
export interface LinkedParty {
	readonly kind: PartyKind;
	readonly name: string;
}

/** The audit entry for a document linked to a party: "Document linked" / "invoice.pdf · Zelena Taxi (Vendor)". */
export function documentLinkedAudit(fileName: string, party: LinkedParty): IntakeAuditEvent {
	return { action: "Document linked", detail: `${fileName} · ${party.name} (${PARTY_KIND_LABELS[party.kind]})` };
}

/** The audit entry for a link taken off a document: "Document unlinked" / "invoice.pdf · Zelena Taxi (Vendor)". */
export function documentUnlinkedAudit(fileName: string, party: LinkedParty): IntakeAuditEvent {
	return { action: "Document unlinked", detail: `${fileName} · ${party.name} (${PARTY_KIND_LABELS[party.kind]})` };
}
